import ApiClient from '@shoflo/api.v2-client';
import {
  CHAT_FULL_ACCESS,
  PRESENTER_BANNER,
  ACCEPTED,
  PRESENTER,
  MAX_RETRIES,
  CAMERA_PRESENTER,
  SOURCE_ROLE_SPEAKER
} from '@shoflo/polymer';
import {
  showNotification,
  showModal,
  setAttendeeName
} from '@shoflo/studio-redux/actions';
import {
  withTimeout,
  getSearchParam,
  recursiveFunction,
  getStreamingStatus
} from '@shoflo/studio-redux/utils';
import {
  KNOCK_ANSWER,
  KNOCK_FAIL
} from '@shoflo/studio-redux/constants/SocketEvents';

import { BROADCAST_TYPE } from 'constants/SearchParams';
import { createSourceFactory } from 'utils/actionUtils';
import { UNAUTHORIZED_ERROR } from '@studio/constants/ModalTypes';

function hasChatAccess(team) {
  return team
    ? team.permissions.some(({ name }) => name === CHAT_FULL_ACCESS)
    : false;
}

export function isValidKnock($action) {
  return (
    $action.payload.type === PRESENTER && $action.payload.status === ACCEPTED
  );
}

export function getApiClient() {
  const { hostname, protocol, port } = (window || {}).location;

  return new ApiClient({
    endpoint: `${protocol}//${hostname}:${port}`,
    proxy: true
  });
}

export function handleFailedCreateAttendee(error, socket, $action, store) {
  console.log(error);
  socket.emit(KNOCK_FAIL, {
    ...$action.payload,
    knockId: $action.payload._id
  });
  if (error?.response?.status === 401) {
    store.dispatch(showModal(UNAUTHORIZED_ERROR));
    return;
  }
  const message = `${$action.payload?.name || 'Presenter'} failed to join!`;
  store.dispatch(
    showNotification(message, {
      timeout: 5500,
      type: 'warning'
    })
  );
  return true;
}

export function sendMeetingToAttendee(socket, knockPayload) {
  // Send the meeting and attendee info over the socket
  socket.emit(
    KNOCK_ANSWER,
    knockPayload,
    withTimeout(
      () => {},
      () => {
        socket.emit(KNOCK_ANSWER, knockPayload);
      },
      3000
    )
  );
}

export function getKnockPayload({
  getState,
  action: $action,
  createdAttendee: { attendeeResponse, chatToken, presenterToken },
  attendeeCameraStatus,
  banners,
  streamEndpoints,
  meeting: { meetingResponse, mixerAttendeeIds },
  attendeeRoster,
  program,
  broadcastSettings,
  team
}) {
  const { AttendeeId: attendeeId } = attendeeResponse.Attendee;
  let { isCameraOn, isMicrophoneOn } = $action.payload || {};
  // Presenter should join with camera ON and MIC OFF by default
  if ('boolean' !== typeof isCameraOn) isCameraOn = true;
  if ('boolean' !== typeof isMicrophoneOn) isMicrophoneOn = false;
  // Allow director to override camera status
  const updatedCameraStatus = {
    ...attendeeCameraStatus,
    [attendeeId]: isCameraOn
  };

  // Get presenter banner
  const presenterBanner = banners.find(
    banner => banner.source.value.templateType === PRESENTER_BANNER
  );

  const streamRecordStatus = {
    isRecording: false,
    isStreaming: getStreamingStatus(streamEndpoints)
  };

  const {
    broadcast: { startOn, endOn } = {},
    broadcastSources: { sources }
  } = getState();

  sources.map(source => {
    if (source.type === CAMERA_PRESENTER) {
      attendeeRoster[source.value] = source.title;
    }
    return source;
  });

  return {
    broadcastId: $action.payload.broadcastId,
    meetingResponse,
    attendeeResponse,
    mixerAttendeeIds,
    socketId: $action.payload.socketId,
    knockId: $action.payload?._id,
    attendeeRoster,
    program,
    chatToken,
    hasChatAccess: hasChatAccess(team),
    attendeeCameraStatus: updatedCameraStatus,
    knockAVStatus: {
      canControlMic: isMicrophoneOn
    },
    muteMeetingAudio: broadcastSettings.muteMeetingAudio || false,
    presenterBanner: presenterBanner || null,
    broadcastSettings,
    streamRecordStatus,
    presenterToken,
    broadcast: {
      endOn,
      startOn,
      type: getSearchParam(getState, BROADCAST_TYPE)
    }
  };
}

/**
 * Generic payload for knock answers that do not require creating an attendee
 * @param getState
 * @param $action
 * @param banners
 * @param streamEndpoints
 * @param attendeeRoster
 * @param program
 * @param broadcastSettings
 * @returns {{socketId, presenterBanner: (*|null), broadcast: {endOn: *, startOn: *, type: *}, attendeeRoster, knockId: *, streamRecordStatus: {isRecording: boolean, isStreaming: *}, broadcastId, broadcastSettings, program, muteMeetingAudio: boolean}}
 */
export function getGenericKnockPayload({
  getState,
  action: $action,
  banners,
  streamEndpoints,
  attendeeRoster,
  program,
  broadcastSettings
}) {
  const presenterBanner = banners.find(
    banner => banner.source.value.templateType === PRESENTER_BANNER
  );

  const streamRecordStatus = {
    isRecording: false,
    isStreaming: getStreamingStatus(streamEndpoints)
  };

  const {
    broadcast: { startOn, endOn } = {},
    broadcastSources: { sources }
  } = getState();

  sources.map(source => {
    const { type, value, title } = source;
    if (type === CAMERA_PRESENTER) {
      attendeeRoster[value] = title;
    }
    return source;
  });

  return {
    broadcastId: $action.payload.broadcastId,
    socketId: $action.payload.socketId,
    knockId: $action.payload?._id,
    attendeeRoster,
    program,
    muteMeetingAudio: broadcastSettings.muteMeetingAudio || false,
    presenterBanner: presenterBanner || null,
    broadcastSettings,
    streamRecordStatus,
    broadcast: {
      endOn,
      startOn,
      type: getSearchParam(getState, BROADCAST_TYPE)
    }
  };
}

export function createAttendee(apiClient, $action, teamId, eventId, meeting) {
  async function createAttendeeRequest() {
    return apiClient.broadcast.createMeetingAttendee(
      teamId,
      eventId,
      $action.payload.broadcastId,
      meeting.meetingResponse.Meeting.MeetingId,
      {
        userType: 'Presenter',
        presenterId: $action.payload.presenterId,
        sessionId: $action.payload.sessionId,
        presenterName: $action.payload.name
      }
    );
  }
  try {
    return recursiveFunction(createAttendeeRequest, MAX_RETRIES);
  } catch (e) {
    throw e;
  }
}

export function createAttendees(
  apiClient,
  teamId,
  eventId,
  broadcastId,
  meeting,
  requestPayload
) {
  async function createAttendeesRequest() {
    return apiClient.broadcast.createMeetingAttendees(
      teamId,
      eventId,
      broadcastId,
      meeting.meetingResponse.Meeting.MeetingId,
      {
        attendees: requestPayload
      }
    );
  }

  try {
    return recursiveFunction(createAttendeesRequest, MAX_RETRIES);
  } catch (e) {
    throw e;
  }
}

export function handleFailedCreateAttendees(
  error,
  store,
  approvedPresenterRequests,
  socket
) {
  console.log(error);
  const message = 'Presenters failed to join!';
  store.dispatch(
    showNotification(message, {
      timeout: 5500,
      type: 'warning'
    })
  );
  approvedPresenterRequests.forEach(req => {
    socket.emit(KNOCK_FAIL, { ...req, knockId: req._id });
  });
  if (error?.response?.status === 401) {
    store.dispatch(showModal(UNAUTHORIZED_ERROR));
    return;
  }
}

export function handleRequest({
  request,
  config,
  store,
  socket,
  attendeeCameraStatus,
  banners,
  streamEndpoints,
  meeting: { meetingResponse, mixerAttendeeIds },
  program,
  attendeeRoster,
  broadcastSettings,
  broadcastId,
  team
}) {
  const { dispatch, getState } = store;

  dispatch(
    setAttendeeName(
      config.attendeeResponse.Attendee.AttendeeId,
      request.name,
      '',
      SOURCE_ROLE_SPEAKER
    )
  );

  const knockPayload = getKnockPayload({
    getState,
    createdAttendee: config,
    attendeeCameraStatus,
    banners,
    streamEndpoints,
    meeting: { meetingResponse, mixerAttendeeIds },
    attendeeRoster,
    program,
    broadcastSettings,
    team,
    action: { payload: { ...request, broadcastId } }
  });

  // Send the meeting and attendee info over the socket
  socket.emit(
    KNOCK_ANSWER,
    knockPayload,
    withTimeout(
      () => {},
      () => {
        socket.emit(KNOCK_ANSWER, knockPayload);
      },
      3000
    )
  );
}

export function sendMeetingToAttendees({
  store,
  socket,
  approvedPresenterRequests,
  createMeetingAttendeesResponse,
  attendeeCameraStatus,
  banners,
  streamEndpoints,
  meeting,
  program,
  attendeeRoster,
  broadcastSettings,
  broadcastId,
  team
}) {
  approvedPresenterRequests.map((req, index) => {
    const config = createMeetingAttendeesResponse[index];
    handleRequest({
      request: req,
      config,
      store,
      socket,
      attendeeCameraStatus,
      banners,
      streamEndpoints,
      meeting,
      program,
      attendeeRoster,
      broadcastSettings,
      broadcastId,
      team
    });
    return req;
  });
  return true;
}

export function getPresenterSourcesToCreate({
  teamId,
  eventId,
  broadcastId,
  lastPresenterOrder,
  approvedPresenterRequests,
  createMeetingAttendeesResponse
}) {
  let currentOrder = lastPresenterOrder;

  const sources = [];

  approvedPresenterRequests.forEach((knock, i) => {
    const createdAttendee = createMeetingAttendeesResponse[i];

    const {
      type,
      title,
      value,
      description = '',
      photo: presenterPhoto,
      userId,
      role,
      cameraUnavailable
    } = createSourceFactory({
      teamId,
      eventId,
      broadcastId,
      title: knock.name,
      description: knock.description,
      attendeeId: createdAttendee.attendeeResponse.Attendee.AttendeeId,
      presenterPhoto: knock?.presenterPhoto,
      userId: knock?.userId,
      role: SOURCE_ROLE_SPEAKER,
      cameraUnavailable: knock.cameraUnavailable || false
    });

    currentOrder = currentOrder + 1;

    sources.push({
      type,
      title,
      value,
      description,
      order: currentOrder,
      photo: presenterPhoto,
      userId,
      role,
      cameraUnavailable
    });
  });

  return sources;
}
